/**
 * In-app browser detection.
 *
 * Detects the common in-app-browser / embedded-WebView user agents that
 * break WebAuthn passkey flows and/or localStorage persistence across
 * sessions. When one of these is detected we block sign-in with a
 * dismissible full-screen notice (see App.js `InAppBrowserBlock`).
 *
 * Detection is UA-based and imperfect — there is no reliable JS API
 * that exposes "am I running inside a WebView?" — but the patterns
 * below catch the meaningful majority of Chrome-Custom-Tabs- and
 * WKWebView-hosted in-app browsers used by common QR-scanner apps and
 * social clients. See karaoke chat 2026-08-25 for the RCA.
 *
 * Returns an object shape:
 *   {
 *     isInApp: boolean,
 *     appName: string | null,   // best-guess friendly label, for logs
 *     matched: string | null,   // the UA substring that matched, for logs
 *   }
 */
export function detectInAppBrowser() {
  const ua = (navigator.userAgent || '').toString();

  // Signals in priority order. First match wins — the label is used
  // for telemetry and not user-facing (the user-facing banner is
  // generic to avoid singling out any one app).
  const signals = [
    // Facebook / Instagram / Messenger
    { re: /FBAN|FBAV|FBIOS|FB_IAB/i,        app: 'Facebook' },
    { re: /Instagram/i,                     app: 'Instagram' },
    { re: /Messenger/i,                     app: 'Messenger' },
    // Twitter / X
    { re: /Twitter/i,                       app: 'Twitter/X' },
    // Snapchat
    { re: /Snapchat/i,                      app: 'Snapchat' },
    // TikTok
    { re: /musical_ly|Bytedance|TikTok/i,   app: 'TikTok' },
    // LinkedIn
    { re: /LinkedInApp/i,                   app: 'LinkedIn' },
    // Line (popular messenger)
    { re: /\bLine\//i,                      app: 'Line' },
    // WeChat
    { re: /MicroMessenger/i,                app: 'WeChat' },
    // Pinterest
    { re: /Pinterest/i,                     app: 'Pinterest' },
    // Reddit
    { re: /Reddit\//i,                      app: 'Reddit' },
    // Discord
    { re: /Discord/i,                       app: 'Discord' },
    // Google iOS app (in-app browser inside Google Search)
    { re: /GSA\//i,                         app: 'Google App' },
    // Amazon iOS in-app browser
    { re: /AmazonWebAppPlatform/i,          app: 'Amazon' },
    // Generic Android WebView signature. Chrome-full has "Chrome/..."
    // WITHOUT the "; wv" marker; WebView-embedded Chrome does have it.
    // This is the catch-all for random QR-scanner apps on Android.
    { re: /Android.*;\s*wv\)|;\s*wv\)/i,    app: 'Android WebView' },
  ];

  for (const s of signals) {
    if (s.re.test(ua)) {
      return { isInApp: true, appName: s.app, matched: s.re.source };
    }
  }

  return { isInApp: false, appName: null, matched: null };
}

/**
 * Best-effort platform label for telemetry / debug UI. Not used for
 * any security or gating decision.
 */
export function getPlatformSummary() {
  const ua = (navigator.userAgent || '').toString();
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isMac = /Macintosh/i.test(ua) && !isIOS;
  const isWindows = /Windows/i.test(ua);
  return {
    ios: isIOS,
    android: isAndroid,
    mac: isMac,
    windows: isWindows,
    ua_length: ua.length,
  };
}
